<template>
  <PremiumMetricWidgetBase :widget="widget" v-slot="{ metric }" :state="state">
    <div class="chart-wrapper">
      <apexchart
        height="100%"
        width="100%"
        :key="series(metric)"
        :options="chartOptions"
        :series="series(metric)"
      />
    </div>
  </PremiumMetricWidgetBase>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import PremiumMetricWidgetBase from './PremiumMetricWidgetBase.vue';
import { formatNumber } from '@/utils/formatNumbers';
import {
  getChartExportSettings,
  getDefaultChartOptions,
  type BaseChartOptions,
} from '@/utils/chartSettings';
import { normalizeDateForTimeComparison } from '@/utils/dateUtils';
import type { WidgetFilter, WidgetObject, WidgetSetting } from '@/types/Widget';

const props = defineProps<{
  widget: WidgetObject;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  cluster?: WidgetSetting;
  day?: WidgetSetting;
}>();

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const state = ref<string | null>(null);

const series = (metric: any) => {
  const firstDate = metric?.data?.data?.arrivals[0]?.time_arrivals[0]?.date;
  if (!firstDate) {
    state.value = 'disabled';
    return [];
  }

  // Get all unique time slots across all days
  const allTimeSlots = new Set<string>();
  metric?.data?.data?.arrivals.forEach((item: any) => {
    item.time_arrivals.forEach((time: any) => {
      const normalizedDate = normalizeDateForTimeComparison(
        firstDate,
        item.time_arrivals[0].date,
        time.date,
      );
      const timeSlot = normalizedDate + 'T' + time.time + 'Z';
      allTimeSlots.add(timeSlot);
    });
  });

  // Convert to sorted array
  const sortedTimeSlots = Array.from(allTimeSlots).sort();

  return (
    metric?.data?.data?.arrivals
      .sort((a: any, b: any) => a.time_arrivals[0].date.localeCompare(b.time_arrivals[0].date))
      .map((item: any) => {
        const itemDate = item.time_arrivals[0].date;
        const dayTotal = item.time_arrivals.reduce(
          (sum: number, time: any) => sum + (time.arrivals ?? 0),
          0,
        );

        // Share of that day's arrivals that fell in each window (not cumulative)
        const existingDataMap = new Map<string, number>();
        item.time_arrivals.forEach((time: any) => {
          const normalizedDate = normalizeDateForTimeComparison(firstDate, itemDate, time.date);
          const timeSlot = normalizedDate + 'T' + time.time + 'Z';
          existingDataMap.set(timeSlot, dayTotal > 0 ? (time.arrivals ?? 0) / dayTotal : 0);
        });

        // Fill in all time slots with data or null
        const filledData = sortedTimeSlots.map((timeSlot) => {
          const value = existingDataMap.get(timeSlot);
          return {
            x: new Date(timeSlot).getTime(),
            y: value !== undefined ? value : null,
          };
        });

        return {
          name: new Date(itemDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            weekday: 'short',
          }),
          data: filledData,
        };
      }) ?? []
  );
};

const chartOptions = computed<BaseChartOptions>(() => ({
  chart: {
    ...defaultChartOptions.value.chart,
    type: 'line',
    toolbar: getChartExportSettings(
      props.widget.name,
      props.area?.name || '',
      props.time?.name || '',
      props.zone?.title || '',
      props.cluster?.title || '',
      props.day?.title || '',
    ),
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  labels: [],
  xaxis: {
    type: 'datetime',
    tooltip: {
      enabled: false,
    },
    labels: {
      hideOverlappingLabels: true,
      rotate: 0,
      format: 'HH:mm',
    },
  },
  yaxis: {
    show: true,
    min: 0,
    labels: {
      formatter: (val: number) => `${(100 * Number(val)).toFixed(0)}%`,
    },
  },
  tooltip: {
    x: {
      formatter: (val: string) => dayjs(val).format('HH:mm'),
    },
    y: {
      formatter: (val: number) => `${formatNumber(100 * Number(val), 2)}%`,
    },
  },
  legend: {
    ...defaultChartOptions.value.legend,
    horizontalAlign: 'center',
  },
}));
</script>
